import { Elements } from '@stripe/react-stripe-js';
import { Skeleton } from 'antd';
import { useEffect, useState } from 'react';

import useStripePromiseHook from '@/hooks/useStripePromiseHook';
import useCreateSetupIntentMutation from '@/v2/domains/Payments/queries/useCreateSetupIntentMutation.hook';

import NewCardFields from './NewCardFields';
import type { CardCredential } from './CardSelection';

interface NewCardCollectorProps {
  confirmLabel: string;
  onConfirm: (credential: CardCredential) => Promise<void>;
  onCancel: () => void;
  footerSlot: HTMLElement | null;
  disabled?: boolean;
}

// <Elements> needs the client secret up front and useStripe only works beneath it,
// hence the wrapper: that split is Stripe's, not ours.
export default function NewCardCollector({
  confirmLabel,
  onConfirm,
  onCancel,
  footerSlot,
  disabled = false,
}: NewCardCollectorProps) {
  const stripePromiseRef = useStripePromiseHook();
  const createSetupIntent = useCreateSetupIntentMutation();
  const [setupIntent, setSetupIntent] = useState<{
    id: string;
    client_secret: string;
  } | null>(null);

  useEffect(() => {
    if (setupIntent) return;
    createSetupIntent.mutate(undefined, { onSuccess: setSetupIntent });
  }, []);

  if (!stripePromiseRef.current || !setupIntent) {
    return <Skeleton active />;
  }

  return (
    <Elements
      stripe={stripePromiseRef.current}
      options={{ clientSecret: setupIntent.client_secret }}
      key={setupIntent.id}
    >
      <NewCardFields
        setupClientSecret={setupIntent.client_secret}
        setupIntentId={setupIntent.id}
        confirmLabel={confirmLabel}
        onConfirm={onConfirm}
        onCancel={onCancel}
        footerSlot={footerSlot}
        disabled={disabled}
      />
    </Elements>
  );
}
